/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from 'echarts-for-react';
import { Button, CardContent, Divider, FormControl, Modal } from "@mui/material";
import { getdDiagnosisResultChainGraphDayByQuery, getDiagnosisResultNoiseByDayByQuery, getDiagnosisResultVibrationSensorsGraphDayByQuery, getEquipmentTemperatureGraphDayByQuery } from "components/axios/equipmentAlertsAxios";
import MainCard from "components/MainCard";
import CustomDatePicker from "components/date_picker/CustomDatePicker";
import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals'; 
import Chip from '@mui/material/Chip';
import useConfig from 'hooks/useConfig';
import { buildBoxPlotData, buildVibrationData } from "utils/graphUtils";
import { updateGradeChart, updateLengthAndElongChart, updateNoiseChart, updateTempChart, updateVibrationAvgChart, updateVibrationBoxPlotChart } from "utils/chartUpdater";
import CommonLargeVibrationDataExcelDownload from "components/CommonLargeVibrationDataExcelDownload";
import { fromServerFormat } from "components/date_picker/dateUtils";

const EquipmentPartSensorsGraphsLayer = ({equipment, sensorPosition, equipmentPartId, equipmentPartPositionName, equipmentPartName, vibrationChartType='AVG', type="S0"}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
    }
    const handleClose = () => setOpen(false);

    // modal 관련
    const { openModal } = useModals();
    const handleAlertClick = (alertMessage) => {
        openModal(modals.alertModal, {
            onClose : () => {
               // console.log(1111);
            },
            message: alertMessage,
        });
    };

    const handleSearch = (event) => {
        event.preventDefault();

        if (searchStartDateText === null || searchStartDateText === '' || searchEndDateText === null || searchEndDateText === '') {
            handleAlertClick(t('message.common.alert.period.select'));
            return;
        }

        if (searchStartDateText !== null && searchStartDateText !== '' && !regexDate.test(searchStartDateText)) {
            handleAlertClick(t('message.common.alert.search.startDate.input'));
            return;
        }

        if (searchEndDateText !== null && searchEndDateText !== '' && !regexDate.test(searchEndDateText)) {
            handleAlertClick(t('message.common.alert.search.endDate.input'));
            return;
        }
        graphInfo();
    };

    const { mode } = useConfig();
    const textColor = mode === 'light' ? '#262626' : '#ffffff';
    const bgColor = mode === 'light' ? '#f9f9fa' : '#2f2f2f';

    const chartDataInit = () => ({
        // darkmode:true,
        chart: {
            background: '#2f2f2f',
            foreColor: '#ffffff',
        },
        textStyle : {
            color : textColor
        },
        tooltip: {
            trigger: 'axis'
        },
        legend: {
            data : [],
            left : 'center',
            top: 0,
            backgroundColor : bgColor,
            textStyle: {
                color: textColor
            }
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: [ ],
            axisLabel: { interval: 'auto', color: textColor }
        },
        yAxis: {
            type: 'value'
        },
        series: [ ]
    });
    
    const regexDate = RegExp(/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/);
    const [searchStartDateText, setSearchStartDateText] = useState('');
    const [searchEndDateText, setSearchEndDateText] = useState('');
    const [excelParam, setExcelParam] = useState([]);

    const chartRef = useRef(null);
    const chartDataRef = useRef(null); 
    const [uiReady, setUiReady] = useState(false);
    const [excelTotalCount, setExcelTotalCount] = useState(0);

    useEffect(() => {
        setSearchStartDateText('');
        setSearchEndDateText('');
        chartDataRef.current = null;
        setUiReady(false);
        setExcelTotalCount(0); // 💡 초기화
    }, [open]);

    // type별 타이틀 텍스트 매핑 헬퍼
    const getGraphTitle = () => {
        const sType = sensorPosition?.sensorType;
        if (type === 'SL') return t('message.common.chip.length.trend.graph');
        if (type === 'SE') return t('message.common.chip.elongation.trend.graph');
        // if (type === 'SB') return t('message.common.chip.grade.history.trend.graph');
        if (type === 'ST') return t('message.common.chip.temperature.trend.graph');
        if (type === 'SV') {
            return sType === 'V' ? t('message.common.chip.vibration.trend.graph') : t('message.common.chip.grade.history.trend.graph');
        }
        return "Trend Graph";
    };

    // 데이터가 실제로 비어있는지 검증하는 헬퍼 함수
    const isDataEmpty = (data) => {
        // console.log(data)
        if (!data) return true;
        if (Array.isArray(data) && data.length === 0) return true;
        // x축 데이터나 수치 배열이 비어있는 경우
        if (data.x && data.x.length === 0) return true;
        if (data.series && data.series.length === 0) return true;
        return false;
    };

    // 💡 단일 차트 컴포넌트 내부에서 모든 타입(V, N, ST, SL, SE, SB)의 setOption을 분기 처리
    const drawIntegratedChart = (chartInstance, currentType, data) => {
        if (!chartInstance) return;

        // 데이터가 없는 경우 처리
        if (isDataEmpty(data)) {
            chartInstance.setOption({
                graphic: {
                    type: 'text',
                    left: 'center',
                    top: 'center',
                    style: {
                        text: t('message.common.text.noData') || 'No Data', // i18n 메시지 적용
                        fill: textColor,
                        font: '16px sans-serif'
                    }
                },
                xAxis: {
                    type: 'category',
                    data: []
                },
                yAxis: {
                    type: 'value'
                },
                series: []
            }, true); // notMerge 옵션을 true로 설정하여 기존 옵션 초기화
            return;
        }

        // 데이터가 존재하는 경우 graphic 제거 후 기존 업데이터 실행
        chartInstance.setOption({ graphic: null });

        // 1. 진동량 센서 분기 (축/반경/수직 3라인)
        if (currentType === 'SV' && sensorPosition?.sensorType === 'V') {
            if (vibrationChartType === "AVG") {
                updateVibrationAvgChart(chartInstance, data, t, textColor, bgColor)
                return;
            } else if (vibrationChartType === "BOX") {
                updateVibrationBoxPlotChart(chartInstance, data, t, textColor, bgColor)
                return;
            }
        }

        // 2. 등급이력 분기 (소음센서 N - Y축 A,B,C,D 치환)
        if (currentType === 'SV' && sensorPosition?.sensorType === 'N') {
            updateNoiseChart(chartInstance, data, t, textColor, bgColor)
            return;
        }

        // 2. 등급이력 분기 (등급타입 SB - rms, skew, kurtosis)
        if (currentType === 'SB') {
            updateGradeChart(chartInstance, data, t, textColor, bgColor);
            return;
        }

        // 3. 온도(ST), 길이(SL), 신율(SE) 분기 (일반 수치형 단일 라인 데이터)
        
        if (currentType === 'ST')
            updateTempChart(chartInstance, data, t, textColor, bgColor);
        else if (currentType === 'SL')
            updateLengthAndElongChart(chartInstance, data, textColor, bgColor, t('message.common.subtitle.length'))
        else if (currentType === 'SE')
            updateLengthAndElongChart(chartInstance, data, textColor, bgColor, t('message.common.subtitle.elongation'))
    };

    const graphInfo = () => {
        if (!equipment) return;

        let graphDateParam = {};
        if (searchStartDateText && searchEndDateText) {
            graphDateParam.startDay = searchStartDateText;
            graphDateParam.endDay = searchEndDateText;
        }
        if (type === "SV" || type === "ST") { // 진동센서SV, 소음센서SV, 온도센서ST
            if(sensorPosition && equipment) {
                let graphParam = {...graphDateParam, "sensorPosition.id":sensorPosition.id, page:0, size:5000};
                // setOptions(null)
                if (type === "SV") {
                    if (sensorPosition.sensorType === 'V') {    // 진동센서
                        const { page, size, ...rest } = graphParam;
                        setExcelParam(rest);
                        getDiagnosisResultVibrationSensorsGraphDayByQuery(graphParam).then((data) => {
                        // getDiagnosisResultVibrationSensorsGraphDayByQuery(sensorPosition.id, {...graphParam, "equipmentId": equipment.id}).then((data) => {
                            // console.log(data)
                            // if (data) {
                            if (data?.averagePage?.content?.length > 0 || data?.rawList.length > 0) {
                                let nextChartData;
                                // console.log(data)
                                if(vibrationChartType === "AVG") {
                                    const averages = data.averagePage?.content ?? [];
                                    averages.sort((a, b) => a.dateTimeLabel.localeCompare(b.dateTimeLabel));
                                    nextChartData = buildVibrationData(averages);
                                } else if (vibrationChartType === "BOX") {
                                    const rawDetails = data.rawList ?? [];
                                    rawDetails.sort((a, b) => a.dateTimeLabel.localeCompare(b.dateTimeLabel));
                                    nextChartData = buildBoxPlotData(rawDetails);
                                }
                                // console.log(nextChartData)

                                const isNotEmpty = nextChartData && nextChartData.x && nextChartData.x.length > 0;

                                chartDataRef.current = isNotEmpty ? nextChartData : null;   
                                setExcelTotalCount(data.averagePage?.totalElements);
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), 'SV', nextChartData);
                            } else {
                                chartDataRef.current = null;
                                setExcelTotalCount(0);
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), 'SV', null);
                            }
                        });
                    } else if (sensorPosition.sensorType === 'N') { // 소음센서
                        getDiagnosisResultNoiseByDayByQuery(graphParam).then((data)=>{
                            if (data && data._embedded) {
                                let list = data._embedded.groupByDailyDiagnosisResultNoises;
                                list.sort((a, b) => a.ymd.localeCompare(b.ymd));

                                const nextChartData = {
                                    x: list.map(item => item.ymd),
                                    rms: list.map(item => Number(item.rms.toFixed(2))),
                                    skew: list.map(item => Number(item.skew.toFixed(2))),
                                    kurt: list.map(item => Number(item.kurtosis.toFixed(2)))
                                };

                                chartDataRef.current = nextChartData;
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, nextChartData);
                            } else {
                                chartDataRef.current = null; 
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, null);
                            }
                        })
                    }
                } else if (type === "ST") {
                    // console.log(process.env.REACT_APP_TEMPERATURE_VIEW_YN)
                    if (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y') {
                        getEquipmentTemperatureGraphDayByQuery(graphParam).then((data)=>{
                            // console.log(data)
                            if (data && data._embedded) {
                                let list = data._embedded.groupByDailyEquipmentTemperatures;
                                list.sort((a, b) => a.ymd.localeCompare(b.ymd));
                
                                const nextChartData = { x: list.map(item => item.ymd) };
                                nextChartData.temps = list.map(item => Number(item.temperature || 0)); // 스펙에 맞춰 필드명 조정 필요

                                chartDataRef.current = nextChartData;
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, nextChartData);
                            } else {
                                chartDataRef.current = null;
                                setUiReady(true);
                                if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, null);
                            }
                        })
                    } else {
                        chartDataRef.current = null; setUiReady(false);
                        // setOptions(null);
                    }
                }
            }
        } else if (type === "SL" || type === "SE") { // 체인(길이센서)SL, 체인(신율센서)SE
            let graphParam = {...graphDateParam, "equipmentPart.id":equipmentPartId, page:0, size:5000};
            // setOptions(null)
            getdDiagnosisResultChainGraphDayByQuery(graphParam).then((data)=>{
                if (data && data._embedded) {
                    let list = data._embedded.groupByDailyDiagnosisResultChains;
                    list.sort((a, b) => a.ymd.localeCompare(b.ymd));

                    const nextChartData = { x: list.map(item => item.ymd) };
                    
                    if (type === 'SL') {
                        nextChartData.values = list.map(item => Number(item.length || 0));      // 스펙에 맞춰 필드명 조정 필요
                    } else if (type === 'SE') {
                        nextChartData.values = list.map(item => Number(item.elongation || 0));  // 스펙에 맞춰 필드명 조정 필요
                    }

                    chartDataRef.current = nextChartData;
                    setUiReady(true);
                    if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, nextChartData);
                } else {
                    chartDataRef.current = null; 
                    setUiReady(true);
                    if (chartRef.current) drawIntegratedChart(chartRef.current.getEchartsInstance(), type, null);
                }
            })
        }
    }

    const dataMappingRules = (column, index, totalElements, startIndex) => {
        if (type === "SV" && sensorPosition.sensorType === 'V') {
            if(vibrationChartType === "AVG") {
                let rowData = [
                    ((column.dateTimeLabel) || ''),
                    ((column.overallShaft?.toFixed(2)) || ''),
                    ((column.overallHorizon?.toFixed(2)) || ''),
                    ((column.overallVertical?.toFixed(2)) || ''),
                ];
                return rowData;
            }
            if(vibrationChartType === "BOX") {
                // console.log(column)
                let rowData = [
                    ((column.dateTimeLabel ? fromServerFormat(column.dateTimeLabel, {format:t('message.common.text.date.format.datetimess')}) : '')),
                    ((column.overallShaft?.toFixed(2)) || ''),
                    ((column.overallHorizon?.toFixed(2)) || ''),
                    ((column.overallVertical?.toFixed(2)) || ''),
                ];
                return rowData;
            }
      
        }
        return null;
    };
    
    const handleChartReady = (instance) => {
        if (chartDataRef.current) {
            drawIntegratedChart(instance, type, chartDataRef.current);
        }
    };

    return (
        <>
            <div>
                {/* <Chip label={(sensorPosition.sensorType === 'V' ? t('message.common.chip.vibration.trend.graph') : t('message.common.chip.grade.history.trend.graph'))+" ("+equipmentPartName+")"} color="primary" /> */}
                <Button variant="contained" style={{padding:"2px 5px",  borderRadius:"100px"}} color="info" onClick={handleOpen} >{t('message.common.btn.period.search')}</Button>
            </div>

            <Modal className="pop_box pop_box1" open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
              <MainCard modal darkTitle content={false}>
                <div style={{display:"flex", alignItems: "center", justifyContent: "space-between", margin: "5px 0 10px 0"}}>
                    <div>
                        <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/popbullet.png`} alt='' /></span>&nbsp;
                        <Chip label={getGraphTitle() + (equipmentPartName ? (" (" + equipmentPartName + ")") : '')} color="success" />
                    </div>

                    <div>
                        <Button variant="contained" color="primary" size="small" onClick={handleClose}>
                            {t('message.common.btn.close')}
                        </Button>
                    </div>
                </div>

                <CardContent>
                    <div style={{display:"flex", alignItems: "center"}}>
                        <FormControl sx={{ m: 1, minWidth: 120 }}>
                            <CustomDatePicker 
                                name={"searchStartDateText"} 
                                value={searchStartDateText} 
                                onChange={(e)=>{setSearchStartDateText(e)}}
                            />
                        </FormControl>
                        <span> ~ </span>
                        <FormControl sx={{ m: 1, minWidth: 120 }}>
                            <CustomDatePicker 
                                name={"searchEndDateText"} 
                                value={searchEndDateText} 
                                onChange={(e)=>{setSearchEndDateText(e)}}
                            />
                        </FormControl>
                        <Button variant="contained" style={{padding:"5px 15px"}} onClick={handleSearch}>
                            <span className="icon_png"><img src={`${process.env.PUBLIC_URL}/img/icon/search.png`} alt='' /></span>{t('message.common.btn.search')}
                        </Button>
                    </div>

                    <div style={{ display:"flex", justifyContent: "space-between", background:"#353535", color:"#fff", height:"50px", width:"800px", padding:"12px", fontSize:"13pt", fontWeight:"bold", borderRadius:"8px"}}>
                        <div>{equipment.name}({equipment.equipmentNo})</div>
                        {type === 'SV' && sensorPosition.sensorType === 'V' && excelTotalCount > 0 &&
                            <CommonLargeVibrationDataExcelDownload 
                                title={`${equipment.name}(${equipment.equipmentNo})_${getGraphTitle() + " (" + equipmentPartName + ")"} ${t('message.common.btn.period.search')}`}
                                excelHeader={[t('message.common.subtitle.date'), t('message.common.subtitle.axis'), t('message.common.subtitle.radius'), t('message.common.subtitle.vertical')]}
                                excelParam={excelParam}
                                fetchApiFn={getDiagnosisResultVibrationSensorsGraphDayByQuery}
                                mappingFn={dataMappingRules}
                                vibrationChartType={vibrationChartType}
                                pageSize={5000}
                                totalCount={excelTotalCount}
                            />
                        }
                    </div>
                    {uiReady &&
                    <div className="row">
                        <div className="mixed-chart" style={{display: "grid"}}>
                        <MainCard content={false}>
                            <ECharts
                                ref={chartRef}
                                notMerge={true}
                                onChartReady={handleChartReady}
                                option={chartDataInit()} 
                                style={{ height: '300px', width: '765px' }}
                                opts={{ width: 'auto' }}
                            />
                        </MainCard>
                        </div>
                    </div>
                    }
                </CardContent>
                <Divider />
            </MainCard>
            </Modal>
        </>
    )
}

export default EquipmentPartSensorsGraphsLayer;